import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useGoogleReviews, ReviewCard, ReviewsSummary, ReviewActions } from '../components/GoogleReviewsSection';

export default function ReviewsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, error } = useGoogleReviews();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <div className="relative pt-32 pb-16 px-4" style={{ background: 'linear-gradient(135deg, #053827 0%, #0a5a40 100%)' }}>
        <div className="container mx-auto max-w-6xl">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="mb-8 text-white border-white hover:bg-white/20"
            data-testid="reviews-back-home"
          >
            <ArrowLeft className="mr-2" size={16} />
            {t('backToHome')}
          </Button>

          <div className="text-center space-y-6">
            <h1 className="text-4xl sm:text-5xl font-bold text-white" data-testid="reviews-page-title">
              {t('reviews.pageTitle', 'Patient Reviews')}
            </h1>
            <p className="text-lg text-emerald-100">
              {t('reviews.pageSubtitle', 'Trusted by patients across Chandrapur')}
            </p>
            {data && <ReviewsSummary data={data} light />}
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-6xl px-4 py-16">
        {error && !data && (
          <p className="text-center text-slate-600 mb-8" data-testid="reviews-error">
            {t('reviews.loadError', 'Unable to load reviews right now. Please visit our Google profile.')}
          </p>
        )}

        {data && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
            {data.reviews.map((review) => (
              <ReviewCard key={`${review.author}-${review.relative_time}`} review={review} />
            ))}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <ReviewActions />
          {data?.google_url && (
            <a href={data.google_url} target="_blank" rel="noopener noreferrer" data-testid="view-on-google-link">
              <Button
                variant="outline"
                size="lg"
                className="text-base px-8 border-2 hover:scale-105 transition-all duration-300"
                style={{ borderColor: 'var(--medical-primary)', color: 'var(--medical-primary)' }}
              >
                {t('reviews.viewOnGoogle', 'View on Google Maps')}
                <ExternalLink className="ml-2" size={16} />
              </Button>
            </a>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
